import axios, { AxiosInstance } from 'axios';

// In development, keep API calls on the frontend origin and let the React dev
// server proxy them to the backend. This avoids exposing a second LAN port.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Debug: Log the API base URL
console.log('🔧 API_BASE_URL:', API_BASE_URL);
console.log('🔧 process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('🔧 NODE_ENV:', process.env.NODE_ENV);

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to every request
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses without auto-logout on error
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

export const authAPI = {
  login: (email: string, password: string) =>
    axiosInstance.post('/auth/login', { email, password }),
  logout: () => axiosInstance.post('/auth/logout'),
  getCurrentUser: () => axiosInstance.get('/auth/me'),
};

// Master Data API
export const masterAPI = {
  getPublications: (type?: string) =>
    axiosInstance.get('/master/publications', { params: { type } }),
  getMachines: () => axiosInstance.get('/master/machines'),
  getMachinesByLocation: (location: string) =>
    axiosInstance.get(`/master/machines-by-location/${location}`),
  getNewsprintTypes: () => axiosInstance.get('/master/newsprint-types'),
  getDowntimeReasons: () => axiosInstance.get('/master/downtime-reasons'),
  getLocations: () => axiosInstance.get('/master/locations'),
  getTowers: () => axiosInstance.get('/master/towers'),
  
  // Newsprint Waste Categories
  getNewsprintWasteCategories: () =>
    axiosInstance.get('/master/newsprint-waste-categories'),
  getNewsprintWasteCategoriesByType: (categoryType: string) =>
    axiosInstance.get(`/master/newsprint-waste-categories/${categoryType}`),
  
  // Plates Waste Categories
  getPlatesWasteCategories: () =>
    axiosInstance.get('/master/plates-waste-categories'),
  getPlatesWasteCategoriesByType: (categoryType: string) =>
    axiosInstance.get(`/master/plates-waste-categories/${categoryType}`),
  
  // CRUD operations for publications
  createPublication: (data: any) =>
    axiosInstance.post('/master/publications', data),
  updatePublication: (id: number, data: any) =>
    axiosInstance.put(`/master/publications/${id}`, data),
  updateVarianceInclusion: (id: number, include: boolean) =>
    axiosInstance.put(`/master/publications/${id}/variance-inclusion`, {
      include_in_copies_variance: include,
    }),
  deletePublication: (id: number) =>
    axiosInstance.delete(`/master/publications/${id}`),
};

export const productionAPI = {
  // Create and read operations
  createRecord: (data: any) => axiosInstance.post('/production/records', data),
  upsertMonthlyInkConsumption: (data: any) =>
    axiosInstance.post('/production/monthly-ink-consumption', data),
  getRecords: (filters?: any) => axiosInstance.get('/production/records', { params: filters }),
  getRecordsByUser: (userId: number) => 
    axiosInstance.get(`/production/records/user/${userId}`),
  getLprsScheduledTimes: () =>
    axiosInstance.get('/production/lprs-scheduled-times'),
  getActiveLprsScheduledTimes: () =>
    axiosInstance.get('/production/lprs-scheduled-times/active'),
  updateLprsScheduledTime: (publicationId: number, data: any) =>
    axiosInstance.put(`/production/lprs-scheduled-times/${publicationId}`, data),
  
  // Update and delete operations
  updateRecord: (recordId: number, data: any) =>
    axiosInstance.put(`/production/records/${recordId}`, data),
  deleteRecord: (recordId: number) =>
    axiosInstance.delete(`/production/records/${recordId}`),
  
  // Analytics endpoints
  getAnalyticsPO: (params: any) => axiosInstance.get('/production/analytics/po', { params }),
  getAnalyticsPrintOrders: (params: any) => axiosInstance.get('/production/analytics/print-orders', { params }),
  getAnalyticsMachine: (params: any) =>
    axiosInstance.get('/production/analytics/machine', { params }),
  getAnalyticsMachineDetailed: (params: any) =>
    axiosInstance.get('/production/analytics/machine-detailed', { params }),
  getAnalyticsLPRS: (params: any) => axiosInstance.get('/production/analytics/lprs', { params }),
  getAnalyticsLPRSTrend: (params: any) => axiosInstance.get('/production/analytics/lprs-trend', { params }),
  getAnalyticsLPRSDelays: (params: any) =>
    axiosInstance.get('/production/analytics/lprs-delays', { params }),
  getAnalyticsNewsprint: (params: any) =>
    axiosInstance.get('/production/analytics/newsprint', { params }),
  getAnalyticsNewsprintKgs: (params: any) =>
    axiosInstance.get('/production/analytics/newsprint-kgs', { params }),
  getAnalyticsPlateConsumption: (params: any) =>
    axiosInstance.get('/production/analytics/plate-consumption', { params }),
  getAnalyticsDowntime: (params: any) =>
    axiosInstance.get('/production/analytics/machine-downtime', { params }),
  getAnalyticsDowntimeByMachine: (params: any) =>
    axiosInstance.get('/production/analytics/downtime-by-machine', { params }),
  getAnalyticsPrintDuration: (params: any) =>
    axiosInstance.get('/production/analytics/print-duration', { params }),
  getAnalyticsScheduleStatus: (params: any) =>
    axiosInstance.get('/production/analytics/schedule-status', { params }),
  getAnalyticsWastes: (params: any) =>
    axiosInstance.get('/production/analytics/wastes', { params }),
  getAnalyticsInk: (params: any) =>
    axiosInstance.get('/production/analytics/ink', { params }),
  getDowntimeDetails: (reasonId: number, params: any) =>
    axiosInstance.get(`/production/analytics/downtime-details/${reasonId}`, { params }),
  
  // Newsprint Waste Entries
  getNewsprintWasteEntries: (recordId: number) =>
    axiosInstance.get(`/production/newsprint-waste-entries/${recordId}`),
  createNewsprintWasteEntry: (data: any) =>
    axiosInstance.post('/production/newsprint-waste-entries', data),
  updateNewsprintWasteEntry: (id: number, data: any) =>
    axiosInstance.put(`/production/newsprint-waste-entries/${id}`, data),
  deleteNewsprintWasteEntry: (id: number) =>
    axiosInstance.delete(`/production/newsprint-waste-entries/${id}`),
  
  // Plates Waste Entries
  getPlatesWasteEntries: (recordId: number) =>
    axiosInstance.get(`/production/plates-waste-entries/${recordId}`),
  createPlatesWasteEntry: (data: any) =>
    axiosInstance.post('/production/plates-waste-entries', data),
  updatePlatesWasteEntry: (id: number, data: any) =>
    axiosInstance.put(`/production/plates-waste-entries/${id}`, data),
  deletePlatesWasteEntry: (id: number) =>
    axiosInstance.delete(`/production/plates-waste-entries/${id}`),
};

// AI Predictions API
export const aiAPI = {
  getMaintenancePredictions: (machineId?: number) => {
    const params = machineId ? { machine_id: machineId } : {};
    return axiosInstance.get('/ai/predictions', { params });
  },
  getMaintenanceRecommendations: (filters?: { publication_ids?: string; start_date?: string; end_date?: string; location?: string }) =>
    axiosInstance.get('/ai/recommendations', { params: filters }),
  runBatchAnalysis: () =>
    axiosInstance.post('/ai/batch-analysis'),
  getModelInfo: () =>
    axiosInstance.get('/ai/model-info'),
  checkMLServiceHealth: () =>
    axiosInstance.get('/ai/health'),
};

export default axiosInstance;
